import { useLayoutEffect, useRef, useState } from "react";

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

// Resample a value series to a new length by linear interpolation, so the line
// can tween between windows with different day counts instead of snapping.
function resampleSeries(values: readonly number[], length: number): number[] {
  if (values.length === length) return [...values];
  if (values.length === 0) return new Array(length).fill(0);
  if (length === 1) return [values[values.length - 1]!];
  return Array.from({ length }, (_, i) => {
    const pos = (i / (length - 1)) * (values.length - 1);
    const lo = Math.floor(pos);
    const hi = Math.min(values.length - 1, lo + 1);
    return values[lo]! + (values[hi]! - values[lo]!) * (pos - lo);
  });
}

// Eases a series of numbers toward its target whenever the values change, so
// the line and the stat numbers animate instead of snapping. `resample` lets
// the line morph across a changed day count; the fixed-length stats don't need
// it.
export function useTweenedSeries(
  target: readonly number[],
  resample: boolean,
  durationMs: number
): number[] {
  const [displayed, setDisplayed] = useState<number[]>(() => [...target]);
  const displayedRef = useRef(displayed);
  const targetRef = useRef(target);
  const rafRef = useRef(0);
  useLayoutEffect(() => {
    displayedRef.current = displayed;
    targetRef.current = target;
  });
  const signature = target.join("|");

  useLayoutEffect(() => {
    const to = [...targetRef.current];
    const current = displayedRef.current;
    const from =
      current.length === to.length
        ? current
        : resample
          ? resampleSeries(current, to.length)
          : to;
    if (from.length === to.length && from.every((v, i) => v === to[i])) {
      if (current !== from) setDisplayed(from);
      return;
    }
    setDisplayed(from);
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutCubic(t);
      setDisplayed(to.map((v, i) => from[i]! + (v - from[i]!) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [signature, resample, durationMs]);

  return displayed;
}
